import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { CTA_COPY } from "../hero/heroContent";
import {
  FINAL_CTA_BODY,
  FINAL_CTA_GAP,
  FINAL_CTA_HEADLINE,
  FINAL_CTA_SCAN_PILLS,
  FINAL_CTA_SHIFTS,
  FINAL_CTA_STAMP,
  FINAL_CTA_TIMELINE_LABELS,
  FINAL_CTA_URGENCY,
} from "./finalCtaContent";

const FinalCtaSection = ({ onOpenSignup, entryLine }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 bg-[#0a0c0f] overflow-hidden"
      data-testid="final-cta-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_100%,rgba(249,115,22,0.1),transparent_65%)] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="badge-mono text-[10px] font-bold tracking-[0.28em] text-orange-400/90 uppercase mb-4">
              {FINAL_CTA_STAMP}
            </p>

            <div className="flex flex-wrap gap-2 mb-5" data-testid="final-cta-scan-pills">
              {FINAL_CTA_SCAN_PILLS.map((pill) => (
                <div
                  key={pill.label}
                  className={`flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border ${
                    pill.value === "24/7"
                      ? "border-orange-500/30 bg-orange-500/10"
                      : "border-white/[0.08] bg-white/[0.02]"
                  }`}
                >
                  <span
                    className={`font-mono font-bold text-sm leading-none ${
                      pill.value === "24/7" ? "text-orange-400" : "text-gray-400"
                    }`}
                  >
                    {pill.value}
                  </span>
                  <span className="text-[9px] font-mono text-gray-600 tracking-wide">
                    {pill.label}
                  </span>
                </div>
              ))}
            </div>

            <h2
              className="font-heading font-bold text-2xl sm:text-3xl lg:text-[2rem] text-[#F3F4F6] leading-[1.15] mb-4"
              data-testid="final-cta-headline"
            >
              <span className="text-gray-400">{FINAL_CTA_HEADLINE.businessLine}</span>{" "}
              <span className="text-white">{FINAL_CTA_HEADLINE.businessHours}</span>
              <br />
              <span className="text-gray-400">{FINAL_CTA_HEADLINE.aiLine}</span>{" "}
              <span className="text-orange-400">{FINAL_CTA_HEADLINE.aiHours}</span>
            </h2>

            <p className="font-body text-sm sm:text-base text-[#9CA3AF] leading-relaxed mb-4">
              {FINAL_CTA_BODY}
            </p>

            <p className="font-body text-sm text-orange-400/90 font-medium mb-8">
              {FINAL_CTA_URGENCY}
            </p>

            <div
              className="rounded-xl border border-orange-500/25 bg-gradient-to-r from-orange-500/10 to-transparent p-5 sm:p-6"
              data-testid="final-cta-launch-pad"
            >
              <button
                type="button"
                onClick={onOpenSignup}
                className="w-full px-6 py-4 text-base sm:text-lg font-bold text-white rounded-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500 transition-all duration-300 shadow-fiery-glow-lg btn-pulse-fiery btn-shine-fiery font-heading"
                data-testid="final-cta-button"
              >
                {CTA_COPY.button}
              </button>
              <p
                className="text-[11px] sm:text-xs text-gray-500 mt-2.5 font-body text-center"
                data-testid="landing-catalog-price"
              >
                {entryLine}
              </p>
            </div>
          </div>

          <motion.div
            className="lg:col-span-7"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={reduceMotion || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: 0.45 }}
            data-testid="final-cta-coverage-board"
          >
            <div className="rounded-2xl border border-white/[0.08] bg-[#0c1018]/80 p-5 sm:p-7">
              <div className="flex items-center justify-between mb-4">
                <span className="badge-mono text-[10px] text-gray-500 tracking-widest">
                  24-HOUR COVERAGE MAP
                </span>
                <span className="badge-mono text-[9px] text-red-400/70 tracking-wider">
                  GAP RISK
                </span>
              </div>

              <div className="mb-2 flex justify-between text-[9px] font-mono text-gray-600">
                {FINAL_CTA_TIMELINE_LABELS.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>

              <div className="relative h-3 rounded-full bg-white/[0.04] border border-white/[0.06] mb-6 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-[33%] right-[8%] bg-red-500/15 border-x border-red-500/20"
                  title="Coverage gap"
                />
                <div className="absolute inset-y-0 left-[8%] w-[25%] bg-gray-500/20 rounded-l-full" />
                <div className="absolute inset-y-0 right-[8%] w-[12%] bg-gray-500/15 rounded-r-full" />
              </div>

              <div className="space-y-4 mb-5">
                {FINAL_CTA_SHIFTS.map((shift) => {
                  const isHero = shift.highlight;

                  return (
                    <div key={shift.id}>
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className={`badge-mono text-[10px] font-bold tracking-wider ${
                            isHero ? "text-orange-400" : "text-gray-500"
                          }`}
                        >
                          {shift.label}
                        </span>
                        <span
                          className={`font-mono font-bold text-lg leading-none ${
                            isHero ? "text-orange-400" : "text-gray-500"
                          }`}
                        >
                          {shift.value}
                        </span>
                      </div>
                      <div className="h-4 rounded-md bg-white/[0.04] border border-white/[0.06] overflow-hidden mb-1.5">
                        <div
                          className={`h-full rounded-md ${
                            isHero
                              ? "bg-gradient-to-r from-orange-500 to-orange-600 shadow-[0_0_16px_rgba(249,115,22,0.35)]"
                              : "bg-gray-600/40"
                          }`}
                          style={{ width: shift.barWidth }}
                        />
                      </div>
                      <p className="text-[10px] font-mono text-gray-600 mb-0.5">{shift.window}</p>
                      <p className="text-[11px] text-gray-500 font-body leading-snug">
                        {shift.detail}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div
                className="rounded-xl border border-red-500/20 bg-red-500/[0.04] px-4 py-3.5 flex items-start gap-4"
                data-testid="final-cta-gap-callout"
              >
                <span className="font-mono font-bold text-2xl text-red-400/80 leading-none flex-shrink-0">
                  {FINAL_CTA_GAP.value}
                </span>
                <div>
                  <p className="font-heading font-semibold text-xs text-red-300/90 mb-0.5">
                    {FINAL_CTA_GAP.label}
                  </p>
                  <p className="text-[10px] sm:text-[11px] text-gray-500 font-body leading-relaxed">
                    {FINAL_CTA_GAP.detail}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FinalCtaSection;
